import type { DataPoint } from '../types';

/**
 * Configuration for history generation.
 */
export interface HistoryGenerationConfig {
    /** Number of historical points to generate (default: 15) */
    pointCount?: number;
    /** How far back to generate history as fraction of viewport (default: 0.3 = 30%) */
    historyFraction?: number;
    /** Volatility as a percentage of price (default: 0.0002 = 0.02%) */
    volatilityPercent?: number;
}

const DEFAULT_HISTORY_CONFIG: Required<HistoryGenerationConfig> = {
    pointCount: 80, // Fewer, less frequent points
    historyFraction: 1.0, // Fill the entire viewport
    volatilityPercent: 0.0002, // Very subtle movement (0.02% per step)
};

/**
 * Generates a small set of synthetic historical data points for chart initialization.
 *
 * Creates a short, calm-looking price line that leads up to the current price.
 * Uses very low volatility to keep values close to the actual price.
 *
 * @param currentPrice - The current/target price (will be the last point's value)
 * @param viewportMs - The chart's viewport duration in milliseconds
 * @param config - Optional configuration for point count and volatility
 * @returns Array of DataPoints sorted by timestamp (oldest first)
 */
export function generateInitialHistory(
    currentPrice: number,
    viewportMs: number,
    config?: HistoryGenerationConfig,
    // time base for the generated timestamps. defaults to wall-clock, but the
    // chart passes its monotonic "chart clock" so seeded history stays aligned.
    nowMs: number = performance.now(),
): DataPoint[] {
    const { pointCount, historyFraction, volatilityPercent } = {
        ...DEFAULT_HISTORY_CONFIG,
        ...config,
    };

    const now = nowMs;
    // Only generate history for a fraction of the viewport (e.g., last 30%)
    const historyDuration = viewportMs * historyFraction;
    const startTime = now - historyDuration;

    // Generate the specified number of points
    const numPoints = Math.max(2, pointCount);
    const points: DataPoint[] = new Array(numPoints);

    // Start with the current price at the current time (rightmost point)
    let price = currentPrice;
    let time = now;

    // Calculate interval between points
    const interval = historyDuration / (numPoints - 1);

    // Generate backward from now to startTime
    for (let i = numPoints - 1; i >= 0; i--) {
        points[i] = {
            timestamp: time,
            value: price,
        };

        // Move backward in time
        time -= interval;

        // Very subtle random walk - keeps values close to currentPrice
        const change = (Math.random() - 0.5) * 2 * volatilityPercent * price;
        price -= change;

        // Keep price positive
        price = Math.max(price, currentPrice * 0.5);
    }

    // Ensure first point's timestamp is exact
    if (points.length > 0) {
        points[0].timestamp = startTime;
    }

    return points;
}
